import React, { useEffect, useMemo, useState } from 'react';
import kpiData from '../data/kpiData';

const filters = ['All', 'Inventory', 'Warehouse', 'Order Fulfillment', 'Procurement'];
const getDefaultKpiLimit = () => (window.innerWidth <= 768 ? 4 : 6);

const KpiLibrary = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [showAllKpis, setShowAllKpis] = useState(false);
  const [defaultKpiLimit, setDefaultKpiLimit] = useState(getDefaultKpiLimit);

  useEffect(() => {
    const handleResize = () => {
      setDefaultKpiLimit(getDefaultKpiLimit());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const filteredKpis = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return kpiData.filter((kpi) => {
      const matchesFilter = activeFilter === 'All' || kpi.category === activeFilter;
      const searchableText = [
        kpi.name,
        kpi.category,
        kpi.project,
        ...kpi.tools,
      ].join(' ').toLowerCase();

      return matchesFilter && (!query || searchableText.includes(query));
    });
  }, [activeFilter, searchTerm]);

  return (
    <section className="kpi-library" id="kpi-library">
      <h2 className="heading">KPI Logic <span>Library</span></h2>

      <div className="kpi-toolbar">
        <div className="kpi-filters" aria-label="KPI category filters">
          {filters.map((filter) => (
            <button
              type="button"
              key={filter}
              className={activeFilter === filter ? 'active' : ''}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>

        <label className="kpi-search">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search KPI, category, project, or tool"
          />
        </label>
      </div>

      {(() => {
        const hasActiveQuery = searchTerm.trim() || activeFilter !== 'All';
        const visibleKpis = hasActiveQuery || showAllKpis ? filteredKpis : filteredKpis.slice(0, defaultKpiLimit);
        const hasHiddenKpis = !hasActiveQuery && filteredKpis.length > visibleKpis.length;

        return (
          <>
            <div className="kpi-grid">
              {visibleKpis.map((kpi) => (
          <article className="kpi-card" key={kpi.id}>
            <div className="kpi-card-top">
              <span className="kpi-badge">{kpi.category}</span>
              <i className="fa-solid fa-chart-line"></i>
            </div>
            <h3>{kpi.name}</h3>
            <p>{kpi.meaning}</p>
            <div className="kpi-project">
              <span>Used Project</span>
              <strong>{kpi.project}</strong>
            </div>
            <button type="button" className="kpi-logic-btn" onClick={() => setSelectedKpi(kpi)}>
              View Logic
            </button>
          </article>
              ))}
            </div>

            {filteredKpis.length > defaultKpiLimit && !hasActiveQuery && (
              <div className="kpi-show-more">
                <span>
                  Showing {visibleKpis.length} of {filteredKpis.length} KPI logic cards
                </span>
                <button type="button" onClick={() => setShowAllKpis((current) => !current)}>
                  {showAllKpis ? 'Show Less' : 'View All KPI Logic'}
                </button>
              </div>
            )}

            {hasHiddenKpis && (
              <p className="kpi-compact-note">Use filters or search to jump directly to a specific KPI.</p>
            )}
          </>
        );
      })()}

      {filteredKpis.length === 0 && (
        <p className="kpi-empty">No KPI logic found for this search.</p>
      )}

      {selectedKpi && (
        <div className="kpi-modal" onClick={() => setSelectedKpi(null)}>
          <div className="kpi-modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="kpi-modal-close" onClick={() => setSelectedKpi(null)} aria-label="Close KPI logic">
              <i className="fa-solid fa-xmark"></i>
            </button>
            <span className="kpi-modal-label">{selectedKpi.category}</span>
            <h3>{selectedKpi.name}</h3>
            <div className="kpi-modal-grid">
              <div className="kpi-modal-panel">
                <h4>Business Meaning</h4>
                <p>{selectedKpi.meaning}</p>
              </div>
              <div className="kpi-modal-panel">
                <h4>Formula / Logic</h4>
                <p>{selectedKpi.formula}</p>
              </div>
              <div className="kpi-modal-panel">
                <h4>Used In Project</h4>
                <p>{selectedKpi.project}</p>
              </div>
              <div className="kpi-modal-panel">
                <h4>Recommended Business Action</h4>
                <p>{selectedKpi.action}</p>
              </div>
              <div className="kpi-modal-panel kpi-modal-tools">
                <h4>Tools Used</h4>
                <div>
                  {selectedKpi.tools.map((tool) => <span key={tool}>{tool}</span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default KpiLibrary;
